import { Component, type ErrorInfo, type ReactNode } from "react";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

/**
 * Global app error boundary — shows a branded fallback instead of a blank page
 * if any uncaught render error bubbles up.
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("[ErrorBoundary]", error, info);
  }

  handleReload = () => {
    try {
      window.location.reload();
    } catch {
      /* noop */
    }
  };

  handleHome = () => {
    try {
      window.location.href = "/";
    } catch {
      /* noop */
    }
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <div
        role="alert"
        style={{
          position: "fixed",
          inset: 0,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          background: "#0D1B1E",
          color: "#F4EDE4",
          fontFamily:
            "system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
          padding: "32px",
          textAlign: "center",
          zIndex: 10000,
        }}
      >
        <div style={{ maxWidth: 440 }}>
          <div
            style={{
              fontSize: "1.5rem",
              fontWeight: 600,
              marginBottom: 12,
              color: "#00C896",
            }}
          >
            Say It NOW
          </div>
          <h1 style={{ fontSize: "1.15rem", margin: "0 0 12px" }}>
            Something went wrong on this page
          </h1>
          <p style={{ opacity: 0.85, lineHeight: 1.5, marginBottom: 24 }}>
            We hit an unexpected error. Try reloading — if it keeps happening,
            open this link in your browser (Chrome / Safari) instead of an in-app
            browser.
          </p>
          <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
            <button
              onClick={this.handleReload}
              style={{
                background: "#00C896",
                color: "#0D1B1E",
                border: 0,
                borderRadius: 999,
                padding: "12px 24px",
                fontSize: "1rem",
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              Reload Page
            </button>
            <button
              onClick={this.handleHome}
              style={{
                background: "transparent",
                color: "#F4EDE4",
                border: "1px solid rgba(244,237,228,0.3)",
                borderRadius: 999,
                padding: "12px 24px",
                fontSize: "1rem",
                fontWeight: 500,
                cursor: "pointer",
              }}
            >
              Go Home
            </button>
          </div>
        </div>
      </div>
    );
  }
}
